/**
 * 맥스매치 Lite/Full 공통 — 처방검수 상세와 동일한 인라인 의약품 테이블.
 * 코드·명 검색, 행 추가, staging(미저장) 인라인 편집, 저장행 수정/저장.
 */
import {
  normalizeDrugCodeSearchResponse,
  type DrugCodeSearchResponse,
} from '@/entities/drug/api/drug.api';
import {
  useDrugSearchByCode,
} from '@/entities/drug/hooks/useDrugs';
import type { MaxMatchMiniMr } from '@/entities/max-match/api/max-match.api';
import { PrescriptionDrugNameSearchDialog } from '@/pages/prescriptions/PrescriptionDrugNameSearchDialog';
import { QuantityInput } from '@/pages/prescriptions/QuantityInput';
import { buildDrugNameSearchKeywordWhenNotEditing } from '@/pages/prescriptions/drugSearchKeywords';
import {
  calcMrTotalAmount,
  formatMiniAmount,
  getMrRowKey,
  withMiniTempRowKey,
} from '@/pages/max-match/maxMatchMiniUtils';
import { DataCard } from '@/shared/components/DataCard';
import { useConfirmDialog } from '@/shared/components/ConfirmDialogProvider';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { MenuWriteGate } from '@/shared/components/MenuWriteGate';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Checkbox } from '@/shared/components/ui/checkbox';
import { Input } from '@/shared/components/ui/input';
import { ToolbarRow } from '@/shared/components/ui/row';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table';
import { handleApiError, useToast } from '@/shared/utils/toast';
import { AlertCircle, CheckCircle2, Plus, Trash2 } from 'lucide-react';
import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';

const QUANTITY_INPUT_CLASS_NAME = 'h-8 w-full min-w-[5.5rem]';

const isSearchEnterKey = (event: React.KeyboardEvent) =>
  event.key === 'Enter' && !event.nativeEvent.isComposing;

export type MaxMatchDrugEditDraft = {
  drugCode: string;
  drugName: string;
  price: number;
  outHospitalQuantity: number;
  inHospitalQuantity: number;
  pharmaCompanyName: string;
  hasDrugInfo: boolean | null;
};

export const isUnsavedMaxMatchMr = (row: MaxMatchMiniMr): boolean =>
  row.mrId == null;

const draftFromRow = (row: MaxMatchMiniMr): MaxMatchDrugEditDraft => ({
  drugCode: row.drugCode ?? '',
  drugName: row.drugName ?? '',
  price: row.price ?? 0,
  outHospitalQuantity: row.outHospitalQuantity ?? 0,
  inHospitalQuantity: row.inHospitalQuantity ?? 0,
  pharmaCompanyName: row.pharmaCompanyName ?? '',
  hasDrugInfo: row.hasDrugInfo ?? null,
});

const applyDraftToRow = (
  row: MaxMatchMiniMr,
  draft: MaxMatchDrugEditDraft,
): MaxMatchMiniMr => ({
  ...row,
  drugCode: draft.drugCode,
  drugName: draft.drugName,
  price: draft.price,
  outHospitalQuantity: draft.outHospitalQuantity,
  inHospitalQuantity: draft.inHospitalQuantity,
  pharmaCompanyName: draft.pharmaCompanyName,
  hasDrugInfo: draft.hasDrugInfo,
});

export interface MaxMatchDrugInlineTableProps {
  rows: MaxMatchMiniMr[];
  loading?: boolean;
  readOnly?: boolean;
  /** MATCHED 등 — 편집·추가·삭제 불가 */
  disabled?: boolean;
  refYm: string;
  onChangeRows: (
    updater: (prev: MaxMatchMiniMr[]) => MaxMatchMiniMr[],
  ) => void;
  /**
   * 저장행(mrId 있음) 인라인 저장.
   * Mini: updateMr API / Full: 로컬 반영 후 dirty
   */
  onPersistSavedRow: (
    row: MaxMatchMiniMr,
    draft: MaxMatchDrugEditDraft,
  ) => Promise<void>;
  /**
   * staging 행 「저장」.
   * Mini: addMiniMr 단건 / Full: 로컬 커밋만 (생략 시 draft→rows만 반영)
   */
  onPersistStagingRow?: (
    row: MaxMatchMiniMr,
    draft: MaxMatchDrugEditDraft,
  ) => Promise<void>;
  /** 저장행 삭제 (mrId 있음). staging은 로컬만 제거 */
  onDeleteSavedRow: (row: MaxMatchMiniMr) => Promise<void>;
  /** 선택 삭제 중 저장행들 — 없으면 onDeleteSavedRow를 순차 호출 */
  onDeleteSavedRows?: (rows: MaxMatchMiniMr[]) => Promise<void>;
  /**
   * 비편집 저장행에서 코드/명 검색 후 즉시 반영.
   * Mini: updateMr / Full: onChangeRows
   */
  onPersistLookup?: (
    row: MaxMatchMiniMr,
    patch: MaxMatchDrugEditDraft,
  ) => Promise<void>;
}

export const MaxMatchDrugInlineTable: React.FC<MaxMatchDrugInlineTableProps> = ({
  rows,
  loading = false,
  readOnly = false,
  disabled = false,
  refYm,
  onChangeRows,
  onPersistSavedRow,
  onPersistStagingRow,
  onDeleteSavedRow,
  onDeleteSavedRows,
  onPersistLookup,
}) => {
  const toast = useToast();
  const confirm = useConfirmDialog();
  const searchDrugByCode = useDrugSearchByCode();
  const canWrite = !readOnly && !disabled;

  const [editingRowKey, setEditingRowKey] = useState<string | number | null>(
    null,
  );
  const [editDraft, setEditDraft] = useState<MaxMatchDrugEditDraft | null>(null);
  const [rowSaving, setRowSaving] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<Array<string | number>>(
    [],
  );
  const drugCodeInputRef = useRef<HTMLInputElement>(null);

  const [nameSearchOpen, setNameSearchOpen] = useState(false);
  const [nameSearchTargetKey, setNameSearchTargetKey] = useState<
    string | number | null
  >(null);
  const [nameSearchPersist, setNameSearchPersist] = useState(false);
  const [nameSearchDraftName, setNameSearchDraftName] = useState('');
  const [nameSearchKeyword, setNameSearchKeyword] = useState('');

  const allRowKeys = useMemo(
    () => rows.map((row) => getMrRowKey(row)),
    [rows],
  );

  const unsavedCount = useMemo(
    () => rows.filter(isUnsavedMaxMatchMr).length,
    [rows],
  );

  const duplicatedCodes = useMemo(() => {
    const counts = new Map<string, number>();
    rows.forEach((row) => {
      const code = row.drugCode?.trim();
      if (!code) return;
      counts.set(code, (counts.get(code) ?? 0) + 1);
    });
    return [...counts.entries()]
      .filter(([, count]) => count > 1)
      .map(([code]) => code);
  }, [rows]);

  const tableTotals = useMemo(() => {
    let totalOut = 0;
    let totalIn = 0;
    let totalAmount = 0;
    rows.forEach((row) => {
      const key = getMrRowKey(row);
      const view =
        editingRowKey === key && editDraft ? editDraft : draftFromRow(row);
      totalOut += view.outHospitalQuantity ?? 0;
      totalIn += view.inHospitalQuantity ?? 0;
      totalAmount +=
        (view.price ?? 0) *
        ((view.outHospitalQuantity ?? 0) + (view.inHospitalQuantity ?? 0));
    });
    return { totalOut, totalIn, totalAmount };
  }, [rows, editingRowKey, editDraft]);

  const allSelected =
    allRowKeys.length > 0 && selectedRowKeys.length === allRowKeys.length;
  const someSelected =
    selectedRowKeys.length > 0 && selectedRowKeys.length < allRowKeys.length;

  useLayoutEffect(() => {
    if (editingRowKey == null) return;
    const input = drugCodeInputRef.current;
    if (!input) return;
    input.focus();
    input.select();
  }, [editingRowKey]);

  const updateEditDraft = (patch: Partial<MaxMatchDrugEditDraft>) => {
    setEditDraft((prev) => (prev ? { ...prev, ...patch } : prev));
  };

  const updateRowByKey = (
    rowKey: string | number,
    patch: Partial<MaxMatchMiniMr>,
  ) => {
    onChangeRows((prev) =>
      prev.map((row) =>
        getMrRowKey(row) === rowKey ? { ...row, ...patch } : row,
      ),
    );
  };

  const removeRowByKey = (rowKey: string | number) => {
    onChangeRows((prev) => prev.filter((row) => getMrRowKey(row) !== rowKey));
    setSelectedRowKeys((prev) => prev.filter((key) => key !== rowKey));
  };

  const handleAddEmptyRow = () => {
    if (!canWrite) return;
    if (editingRowKey != null) {
      toast.error('편집 중인 행을 먼저 저장하거나 취소해 주세요.');
      return;
    }
    const newRow = withMiniTempRowKey({
      mrId: undefined,
      drugCode: '',
      drugName: '',
      price: 0,
      outHospitalQuantity: 0,
      inHospitalQuantity: 0,
      pharmaCompanyName: '',
      hasDrugInfo: null,
    });
    const rowKey = getMrRowKey(newRow);
    onChangeRows((prev) => [...prev, newRow]);
    setEditingRowKey(rowKey);
    setEditDraft(draftFromRow(newRow));
    toast.success('입력용 행이 추가되었습니다.');
  };

  const handleStartEditRow = (row: MaxMatchMiniMr) => {
    if (!canWrite || isUnsavedMaxMatchMr(row)) return;
    const rowKey = getMrRowKey(row);
    setEditingRowKey(rowKey);
    setEditDraft(draftFromRow(row));
  };

  const handleCancelEditRow = (sourceRow?: MaxMatchMiniMr) => {
    const rowKey = editingRowKey;
    const draft = editDraft;
    setEditingRowKey(null);
    setEditDraft(null);

    if (
      rowKey != null &&
      sourceRow &&
      isUnsavedMaxMatchMr(sourceRow) &&
      !draft?.drugCode &&
      !draft?.drugName &&
      Number(draft?.outHospitalQuantity ?? 0) === 0 &&
      Number(draft?.inHospitalQuantity ?? 0) === 0
    ) {
      removeRowByKey(rowKey);
    }
  };

  const handleSaveEditRow = async (row: MaxMatchMiniMr) => {
    if (!editDraft) return;
    if (!editDraft.drugCode.trim()) {
      toast.error('의약품 코드를 입력해 주세요.');
      return;
    }

    setRowSaving(true);
    try {
      if (isUnsavedMaxMatchMr(row)) {
        if (onPersistStagingRow) {
          await onPersistStagingRow(row, editDraft);
        } else {
          updateRowByKey(getMrRowKey(row), applyDraftToRow(row, editDraft));
        }
        setEditingRowKey(null);
        setEditDraft(null);
        toast.success('행 정보가 수정되었습니다.');
        return;
      }

      await onPersistSavedRow(row, editDraft);
      setEditingRowKey(null);
      setEditDraft(null);
      toast.success('행 정보가 수정되었습니다.');
    } catch (e) {
      handleApiError(e, toast);
    } finally {
      setRowSaving(false);
    }
  };

  const handleDrugCodeSearch = async (
    row: MaxMatchMiniMr,
    options?: { persist?: boolean; draftCode?: string },
  ) => {
    const rowKey = getMrRowKey(row);
    const isEditing = editingRowKey === rowKey && editDraft != null;
    const drugCode = (
      options?.draftCode ??
      (isEditing ? editDraft?.drugCode : row.drugCode) ??
      ''
    ).trim();

    if (!drugCode) {
      toast.error('의약품 코드를 입력해 주세요.');
      return;
    }
    if (!refYm) {
      toast.error('처방 년월 정보가 없습니다.');
      return;
    }

    try {
      const found = await searchDrugByCode.mutateAsync({
        drugCode,
        refYm,
      });
      const next: MaxMatchDrugEditDraft = {
        drugCode: found.drugCode,
        drugName: found.drugName,
        price: found.price ?? 0,
        pharmaCompanyName: found.pharmaCompanyName ?? '',
        hasDrugInfo: true,
        outHospitalQuantity: isEditing
          ? (editDraft!.outHospitalQuantity ?? 0)
          : (row.outHospitalQuantity ?? 0),
        inHospitalQuantity: isEditing
          ? (editDraft!.inHospitalQuantity ?? 0)
          : (row.inHospitalQuantity ?? 0),
      };

      if (isUnsavedMaxMatchMr(row) || isEditing) {
        if (isEditing) {
          updateEditDraft(next);
        } else {
          updateRowByKey(rowKey, applyDraftToRow(row, next));
        }
        toast.success('의약품 정보를 불러왔습니다.');
        return;
      }

      if (options?.persist && onPersistLookup) {
        await onPersistLookup(row, next);
        toast.success('의약품 정보를 적용했습니다.');
      } else if (options?.persist) {
        updateRowByKey(rowKey, applyDraftToRow(row, next));
        toast.success('의약품 정보를 적용했습니다.');
      }
    } catch (e) {
      handleApiError(e, toast);
      const cleared = {
        drugName: '',
        price: 0,
        pharmaCompanyName: '',
        hasDrugInfo: false as boolean | null,
      };
      if (isEditing) {
        updateEditDraft(cleared);
      } else if (isUnsavedMaxMatchMr(row)) {
        updateRowByKey(rowKey, cleared);
      }
    }
  };

  const handleStagingDrugCodeBlur = async (row: MaxMatchMiniMr) => {
    if (!isUnsavedMaxMatchMr(row)) return;
    const drugCode = row.drugCode?.trim();
    if (!drugCode || !refYm) return;
    try {
      const found = await searchDrugByCode.mutateAsync({ drugCode, refYm });
      updateRowByKey(getMrRowKey(row), {
        drugCode: found.drugCode,
        drugName: found.drugName,
        price: found.price ?? row.price,
        pharmaCompanyName: found.pharmaCompanyName ?? row.pharmaCompanyName,
        hasDrugInfo: true,
      });
    } catch {
      updateRowByKey(getMrRowKey(row), { hasDrugInfo: false });
    }
  };

  const openNameSearchForRow = (
    row: MaxMatchMiniMr,
    options?: { persist?: boolean },
  ) => {
    setNameSearchTargetKey(getMrRowKey(row));
    setNameSearchPersist(Boolean(options?.persist));
    setNameSearchDraftName(row.drugName ?? '');
    setNameSearchKeyword(
      buildDrugNameSearchKeywordWhenNotEditing(row.drugName) ||
        (row.drugName ?? ''),
    );
    setNameSearchOpen(true);
  };

  const applyNameSearchSelection = async (selected: DrugCodeSearchResponse) => {
    if (nameSearchTargetKey == null) return;

    const targetKey = nameSearchTargetKey;
    const normalized = normalizeDrugCodeSearchResponse(selected);
    if (!normalized.pharmaCompanyName && normalized.drugCode && refYm) {
      try {
        const found = await searchDrugByCode.mutateAsync({
          drugCode: normalized.drugCode,
          refYm,
        });
        const lookedUp = normalizeDrugCodeSearchResponse(found);
        if (!normalized.pharmaCompanyName) {
          normalized.pharmaCompanyName = lookedUp.pharmaCompanyName;
        }
        if (!normalized.price) {
          normalized.price = lookedUp.price;
        }
      } catch {
        // 이름 검색만으로도 반영
      }
    }

    const target = rows.find((row) => getMrRowKey(row) === targetKey);
    if (!target) return;

    const next: MaxMatchDrugEditDraft = {
      drugCode: normalized.drugCode,
      drugName: normalized.drugName,
      price: normalized.price ?? 0,
      pharmaCompanyName: normalized.pharmaCompanyName ?? '',
      hasDrugInfo: true,
      outHospitalQuantity:
        editingRowKey === targetKey && editDraft
          ? editDraft.outHospitalQuantity
          : (target.outHospitalQuantity ?? 0),
      inHospitalQuantity:
        editingRowKey === targetKey && editDraft
          ? editDraft.inHospitalQuantity
          : (target.inHospitalQuantity ?? 0),
    };

    if (editingRowKey === targetKey || isUnsavedMaxMatchMr(target)) {
      if (editingRowKey === targetKey) {
        updateEditDraft(next);
      }
      if (isUnsavedMaxMatchMr(target)) {
        updateRowByKey(targetKey, applyDraftToRow(target, next));
      }
      toast.success('의약품 명 검색 결과를 반영했습니다.');
      return;
    }

    if (nameSearchPersist && onPersistLookup) {
      try {
        await onPersistLookup(target, next);
        toast.success('의약품 정보를 저장했습니다.');
      } catch (e) {
        handleApiError(e, toast);
      }
    } else if (nameSearchPersist) {
      updateRowByKey(targetKey, applyDraftToRow(target, next));
      toast.success('의약품 정보를 저장했습니다.');
    }
  };

  const handleDeleteRow = async (row: MaxMatchMiniMr) => {
    const rowKey = getMrRowKey(row);
    const ok = await confirm({
      title: '삭제 확인',
      description: '이 의약품을 삭제하시겠습니까?',
      confirmLabel: '삭제',
      variant: 'destructive',
    });
    if (!ok) return;

    try {
      if (isUnsavedMaxMatchMr(row)) {
        if (editingRowKey === rowKey) {
          setEditingRowKey(null);
          setEditDraft(null);
        }
        removeRowByKey(rowKey);
        toast.success('목록에서 삭제했습니다.');
        return;
      }
      await onDeleteSavedRow(row);
      if (editingRowKey === rowKey) {
        setEditingRowKey(null);
        setEditDraft(null);
      }
      setSelectedRowKeys((prev) => prev.filter((key) => key !== rowKey));
      toast.success('의약품을 삭제했습니다.');
    } catch (e) {
      handleApiError(e, toast);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedRowKeys.length === 0) return;
    const ok = await confirm({
      title: '삭제 확인',
      description: `선택한 의약품 ${selectedRowKeys.length}건을 삭제합니다.`,
      confirmLabel: '삭제',
      variant: 'destructive',
    });
    if (!ok) return;

    const selectedSet = new Set(selectedRowKeys);
    const stagingToRemove = rows.filter(
      (row) =>
        selectedSet.has(getMrRowKey(row)) && isUnsavedMaxMatchMr(row),
    );
    const savedToDelete = rows.filter(
      (row) =>
        selectedSet.has(getMrRowKey(row)) && !isUnsavedMaxMatchMr(row),
    );

    try {
      if (stagingToRemove.length > 0) {
        const removeKeys = new Set(
          stagingToRemove.map((row) => getMrRowKey(row)),
        );
        onChangeRows((prev) =>
          prev.filter((row) => !removeKeys.has(getMrRowKey(row))),
        );
      }
      if (savedToDelete.length > 0) {
        if (onDeleteSavedRows) {
          await onDeleteSavedRows(savedToDelete);
        } else {
          for (const row of savedToDelete) {
            await onDeleteSavedRow(row);
          }
        }
      }
      if (
        editingRowKey != null &&
        selectedSet.has(editingRowKey)
      ) {
        setEditingRowKey(null);
        setEditDraft(null);
      }
      setSelectedRowKeys([]);
      toast.success('선택한 의약품을 삭제했습니다.');
    } catch (e) {
      handleApiError(e, toast);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-row flex-wrap items-center justify-between gap-3">
        <div className="space-y-2">
          <h3 className="flex items-center gap-2 text-lg font-semibold">
            의약품 목록
            {unsavedCount > 0 ? (
              <Badge variant="secondary">미등록 {unsavedCount}건</Badge>
            ) : null}
          </h3>
          <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <span
                className="inline-block h-3 w-3 rounded-sm border border-amber-200 bg-amber-50"
                aria-hidden
              />
              동일 의약품 코드 중복
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span
                className="inline-block h-3 w-3 rounded-sm border border-sky-200 bg-sky-50"
                aria-hidden
              />
              미저장 행 (OCR·행 추가, 일괄 추가 전)
            </span>
          </div>
        </div>
        <MenuWriteGate>
          <ToolbarRow gap={8}>
            <Button
              size="sm"
              variant="destructive"
              disabled={!canWrite || selectedRowKeys.length === 0}
              onClick={() => void handleDeleteSelected()}
            >
              선택 삭제
            </Button>
            <Button
              size="sm"
              onClick={handleAddEmptyRow}
              disabled={!canWrite || editingRowKey != null}
            >
              <Plus className="mr-2 h-4 w-4" />
              행 추가
            </Button>
          </ToolbarRow>
        </MenuWriteGate>
      </div>

      <DataCard>
        {loading ? (
          <div className="py-12">
            <LoadingSpinner tip="의약품 목록을 불러오는 중..." />
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-14">
                  <Checkbox
                    checked={
                      allSelected ? true : someSelected ? 'indeterminate' : false
                    }
                    onCheckedChange={(checked) =>
                      setSelectedRowKeys(checked === true ? allRowKeys : [])
                    }
                    aria-label="전체 선택"
                    disabled={!canWrite}
                  />
                </TableHead>
                <TableHead className="min-w-40">코드</TableHead>
                <TableHead>의약품명</TableHead>
                <TableHead>가격</TableHead>
                <TableHead className="min-w-28">원외</TableHead>
                <TableHead className="min-w-28">원내</TableHead>
                <TableHead>제약사</TableHead>
                <TableHead>정보</TableHead>
                <TableHead>합계</TableHead>
                <TableHead className="w-24">작업</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={10}
                    className="py-8 text-center text-muted-foreground"
                  >
                    등록된 의약품이 없습니다.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => {
                  const rowKey = getMrRowKey(row);
                  const isStaging = isUnsavedMaxMatchMr(row);
                  const isEditing =
                    editingRowKey === rowKey && editDraft != null;
                  const showEditUi = canWrite && (isStaging || isEditing);
                  const view = isEditing && canWrite ? editDraft! : draftFromRow(row);
                  const anotherRowEditing =
                    editingRowKey != null && editingRowKey !== rowKey;
                  const total = calcMrTotalAmount({
                    ...row,
                    ...view,
                  });
                  const isDuplicate =
                    Boolean(view.drugCode?.trim()) &&
                    duplicatedCodes.includes(view.drugCode.trim());

                  return (
                    <TableRow
                      key={rowKey}
                      className={
                        isDuplicate
                          ? 'bg-amber-50'
                          : isStaging
                            ? 'bg-sky-50'
                            : undefined
                      }
                    >
                      <TableCell>
                        <Checkbox
                          checked={selectedRowKeys.includes(rowKey)}
                          onCheckedChange={(checked) =>
                            setSelectedRowKeys((prev) =>
                              checked === true
                                ? [...prev, rowKey]
                                : prev.filter((key) => key !== rowKey),
                            )
                          }
                          aria-label={`${view.drugCode || rowKey} 선택`}
                          disabled={!canWrite}
                        />
                      </TableCell>
                      <TableCell className="min-w-40">
                        {showEditUi ? (
                          <div className="flex w-full min-w-0 items-center gap-1">
                            <Input
                              ref={isEditing ? drugCodeInputRef : undefined}
                              aria-label="의약품 코드"
                              className="h-8 min-w-0 flex-1"
                              value={view.drugCode ?? ''}
                              onChange={(e) => {
                                if (isEditing) {
                                  updateEditDraft({ drugCode: e.target.value });
                                } else {
                                  updateRowByKey(rowKey, {
                                    drugCode: e.target.value,
                                  });
                                }
                              }}
                              onBlur={() => {
                                if (isStaging) {
                                  void handleStagingDrugCodeBlur(row);
                                }
                              }}
                              onKeyDown={(e) => {
                                if (!isSearchEnterKey(e)) return;
                                e.preventDefault();
                                void handleDrugCodeSearch(row, {
                                  draftCode: e.currentTarget.value,
                                });
                              }}
                            />
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="shrink-0"
                              disabled={
                                !String(view.drugCode ?? '').trim() ||
                                searchDrugByCode.isPending
                              }
                              onClick={() =>
                                void handleDrugCodeSearch(row, {
                                  draftCode: isEditing
                                    ? editDraft?.drugCode
                                    : undefined,
                                })
                              }
                            >
                              검색
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1">
                            <span>
                              {row.drugCode || (
                                <span className="text-muted-foreground">
                                  의약품 코드 입력
                                </span>
                              )}
                            </span>
                            <MenuWriteGate>
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                disabled={
                                  !row.drugCode ||
                                  anotherRowEditing ||
                                  !canWrite ||
                                  searchDrugByCode.isPending
                                }
                                onClick={() =>
                                  void handleDrugCodeSearch(row, {
                                    persist: true,
                                  })
                                }
                              >
                                검색
                              </Button>
                            </MenuWriteGate>
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {showEditUi ? (
                          <div className="flex items-center gap-1">
                            <Input
                              aria-label="의약품명"
                              className="h-8 min-w-32"
                              value={view.drugName ?? ''}
                              onChange={(e) => {
                                if (isEditing) {
                                  updateEditDraft({ drugName: e.target.value });
                                } else {
                                  updateRowByKey(rowKey, {
                                    drugName: e.target.value,
                                  });
                                }
                              }}
                              onKeyDown={(e) => {
                                if (!isSearchEnterKey(e)) return;
                                e.preventDefault();
                                const drugName = e.currentTarget.value.trim();
                                if (!drugName) return;
                                openNameSearchForRow(
                                  isEditing
                                    ? { ...row, ...editDraft, drugName }
                                    : { ...row, drugName },
                                );
                              }}
                            />
                            <Button
                              type="button"
                              size="sm"
                              className="ruda-button"
                              disabled={!String(view.drugName ?? '').trim()}
                              onClick={() =>
                                openNameSearchForRow(
                                  isEditing ? { ...row, ...editDraft } : row,
                                )
                              }
                            >
                              검색
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1">
                            <span className="max-w-48 truncate">
                              {row.drugName || '-'}
                            </span>
                            <MenuWriteGate>
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                disabled={
                                  !row.drugName ||
                                  anotherRowEditing ||
                                  !canWrite
                                }
                                onClick={() =>
                                  openNameSearchForRow(row, { persist: true })
                                }
                              >
                                검색
                              </Button>
                            </MenuWriteGate>
                          </div>
                        )}
                      </TableCell>
                      <TableCell>{formatMiniAmount(view.price)}</TableCell>
                      <TableCell className="min-w-28">
                        {showEditUi ? (
                          <QuantityInput
                            aria-label="원외 수량"
                            className={QUANTITY_INPUT_CLASS_NAME}
                            value={view.outHospitalQuantity ?? 0}
                            onChange={(value) => {
                              if (isEditing) {
                                updateEditDraft({ outHospitalQuantity: value });
                              } else {
                                updateRowByKey(rowKey, {
                                  outHospitalQuantity: value,
                                });
                              }
                            }}
                          />
                        ) : (
                          formatMiniAmount(row.outHospitalQuantity)
                        )}
                      </TableCell>
                      <TableCell className="min-w-28">
                        {showEditUi ? (
                          <QuantityInput
                            aria-label="원내 수량"
                            className={QUANTITY_INPUT_CLASS_NAME}
                            value={view.inHospitalQuantity ?? 0}
                            onChange={(value) => {
                              if (isEditing) {
                                updateEditDraft({ inHospitalQuantity: value });
                              } else {
                                updateRowByKey(rowKey, {
                                  inHospitalQuantity: value,
                                });
                              }
                            }}
                          />
                        ) : (
                          formatMiniAmount(row.inHospitalQuantity)
                        )}
                      </TableCell>
                      <TableCell>{view.pharmaCompanyName || '-'}</TableCell>
                      <TableCell>
                        {view.hasDrugInfo === true ? (
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                        ) : view.hasDrugInfo === false ? (
                          <AlertCircle className="h-4 w-4 text-red-500" />
                        ) : (
                          <span className="text-xs text-muted-foreground">
                            의약품 일괄 추가 필요
                          </span>
                        )}
                      </TableCell>
                      <TableCell>{formatMiniAmount(total)}</TableCell>
                      <TableCell>
                        <MenuWriteGate>
                          <div className="flex gap-1">
                            {isEditing ? (
                              <>
                                <Button
                                  size="sm"
                                  className="ruda-button"
                                  disabled={rowSaving}
                                  onClick={() => void handleSaveEditRow(row)}
                                >
                                  저장
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled={rowSaving}
                                  onClick={() => handleCancelEditRow(row)}
                                >
                                  취소
                                </Button>
                              </>
                            ) : null}
                            {!isStaging && !isEditing ? (
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={!canWrite || anotherRowEditing}
                                onClick={() => handleStartEditRow(row)}
                              >
                                수정
                              </Button>
                            ) : null}
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={!canWrite || anotherRowEditing}
                              onClick={() => void handleDeleteRow(row)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </MenuWriteGate>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
              {rows.length > 0 ? (
                <TableRow className="bg-muted/40 font-medium">
                  <TableCell colSpan={4} className="text-right">
                    합:
                  </TableCell>
                  <TableCell>
                    총 원외 처방 수량
                    <br />
                    {tableTotals.totalOut.toLocaleString()}
                  </TableCell>
                  <TableCell>
                    총 원내 처방 수량
                    <br />
                    {tableTotals.totalIn.toLocaleString()}
                  </TableCell>
                  <TableCell colSpan={2}>
                    총 처방 금액
                    <br />₩{tableTotals.totalAmount.toLocaleString()}
                  </TableCell>
                  <TableCell colSpan={2} />
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        )}
      </DataCard>

      <PrescriptionDrugNameSearchDialog
        open={nameSearchOpen}
        onOpenChange={setNameSearchOpen}
        refYm={refYm}
        initialKeyword={nameSearchKeyword}
        draftDrugName={nameSearchDraftName}
        onSelect={(drug) => {
          void applyNameSearchSelection(drug);
          setNameSearchOpen(false);
        }}
      />
    </div>
  );
};
